import { useState, useEffect, useRef, useCallback } from 'react';
import apiClient from '../api-client.js';

/**
 * Hook for polling Celery task status
 * 
 * @param {string|null} taskId - The Celery task ID to poll (null to disable)
 * @param {Object} options - Configuration options
 * @param {Function} options.onSuccess - Called when task completes successfully
 * @param {Function} options.onError - Called when task fails
 * @param {Function} options.onUpdate - Called on each poll (optional)
 * @param {number} options.interval - Polling interval in ms (default: 2000)
 * @param {number} options.maxAttempts - Max polling attempts (default: 60)
 * 
 * @returns {Object} { status, isPolling, error, stopPolling }
 */
export function useTaskPolling(taskId, options = {}) {
    const {
        onSuccess = () => { },
        onError = () => { },
        onUpdate = () => { },
        interval = 2000,
        maxAttempts = 60,
    } = options;

    const [status, setStatus] = useState(null);
    const [isPolling, setIsPolling] = useState(false);
    const [error, setError] = useState(null);

    const intervalRef = useRef(null);
    const attemptsRef = useRef(0);
    const taskIdRef = useRef(taskId);

    // Update ref when taskId changes
    useEffect(() => {
        taskIdRef.current = taskId;
    }, [taskId]);

    const stopPolling = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        setIsPolling(false);
        attemptsRef.current = 0;
    }, []);

    const pollTaskStatus = useCallback(async () => {
        if (!taskIdRef.current) return;

        try {
            const response = await apiClient.get(`/content-generation/task-status/${taskIdRef.current}/`);
            const data = response.data;

            setStatus(data.status);
            onUpdate(data);

            if (data.status === 'SUCCESS') {
                stopPolling();
                onSuccess(data.result);
            } else if (data.status === 'FAILURE') {
                stopPolling();
                setError(data.error || 'Task failed');
                onError(data.error || 'Task failed');
            } else {
                // Still pending or running
                attemptsRef.current += 1;
                if (attemptsRef.current >= maxAttempts) {
                    stopPolling();
                    const timeoutError = 'Task timeout: exceeded max polling attempts';
                    setError(timeoutError);
                    onError(timeoutError);
                }
            }
        } catch (err) {
            console.error('Error polling task status:', err);
            attemptsRef.current += 1;
            if (attemptsRef.current >= maxAttempts) {
                stopPolling();
                const pollError = 'Failed to poll task status';
                setError(pollError);
                onError(pollError);
            }
        }
    }, [maxAttempts, onError, onSuccess, onUpdate, stopPolling]);

    // Start polling when taskId changes
    useEffect(() => {
        if (!taskId) {
            stopPolling();
            setStatus(null);
            setError(null);
            return;
        }

        // Reset state for new task
        setStatus('PENDING');
        setError(null);
        setIsPolling(true);
        attemptsRef.current = 0;

        // Initial poll
        pollTaskStatus();

        // Start interval
        intervalRef.current = setInterval(pollTaskStatus, interval);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taskId, interval]);

    return {
        status,
        isPolling,
        error,
        stopPolling,
    };
}
